import React, { useMemo, useState } from 'react';
import './SchoolUsers.css';

function SchoolUsers({ users, schoolId, onRefreshUsers }) {
  const [resetUser, setResetUser] = useState(null);   // user whose password we're resetting
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Delete-account flow (type-to-confirm).
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const openDelete = (user) => { setDeleteUser(user); setDeleteConfirmText(''); setDeleteError(null); };
  const closeDelete = () => { if (deleting) return; setDeleteUser(null); setDeleteConfirmText(''); setDeleteError(null); };

  // Edit-role flow.
  const [roleUser, setRoleUser] = useState(null);
  const [roleDraft, setRoleDraft] = useState('teacher');
  const [savingRole, setSavingRole] = useState(false);
  const [roleError, setRoleError] = useState(null);

  const openRole = (user) => { setRoleUser(user); setRoleDraft((user.role || 'teacher').toLowerCase()); setRoleError(null); };
  const closeRole = () => { if (savingRole) return; setRoleUser(null); setRoleError(null); };

  const handleSaveRole = async () => {
    if (savingRole || !roleUser) return;
    setSavingRole(true);
    setRoleError(null);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/users/${roleUser.id}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Basic ' + btoa(`${username}:${password}`) },
        body: JSON.stringify({ role: roleDraft })
      });
      if (response.ok) {
        setRoleUser(null);
        if (onRefreshUsers) await onRefreshUsers();
      } else {
        const data = await response.json().catch(() => ({}));
        setRoleError(data.error || 'Failed to update role. Please try again.');
      }
    } catch (err) {
      console.error('Error updating role:', err);
      setRoleError('Error updating role. Please try again.');
    } finally {
      setSavingRole(false);
    }
  };

  // Make-district-account flow: swap a school user into a brand-new district account.
  const [mkDistrictUser, setMkDistrictUser] = useState(null);
  const [mkDistrictBusy, setMkDistrictBusy] = useState(false);
  const [mkDistrictError, setMkDistrictError] = useState(null);
  const openMkDistrict = (user) => { setMkDistrictUser(user); setMkDistrictError(null); };
  const closeMkDistrict = () => { if (mkDistrictBusy) return; setMkDistrictUser(null); setMkDistrictError(null); };

  const handleMakeDistrict = async () => {
    if (mkDistrictBusy || !mkDistrictUser) return;
    setMkDistrictBusy(true);
    setMkDistrictError(null);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/users/${mkDistrictUser.id}/make-district-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Basic ' + btoa(`${username}:${password}`) }
      });
      if (response.ok) {
        setMkDistrictUser(null);
        alert("Done. They now have a district account with the same login, and this school has moved into the new district. They'll be asked to name the district the next time they sign in.");
        if (onRefreshUsers) await onRefreshUsers();
      } else {
        const data = await response.json().catch(() => ({}));
        setMkDistrictError(data.error || 'Failed. Please try again.');
      }
    } catch (err) {
      console.error('Error making district account:', err);
      setMkDistrictError('Error. Please try again.');
    } finally {
      setMkDistrictBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleting || !deleteUser) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/users/${deleteUser.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Basic ' + btoa(`${username}:${password}`) }
      });
      if (response.ok) {
        setDeleteUser(null);
        setDeleteConfirmText('');
        if (onRefreshUsers) await onRefreshUsers();
      } else {
        setDeleteError('Failed to delete account. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting account:', err);
      setDeleteError('Error deleting account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Sort users: admins first, then alphabetically by name
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const roleOrder = { 'administrator': 0, 'teacher': 1, '': 2 };
      const roleA = roleOrder[a.role] ?? 99;
      const roleB = roleOrder[b.role] ?? 99;
      if (roleA !== roleB) {
        return roleA - roleB;
      }
      return a.name.localeCompare(b.name);
    });
  }, [users]);

  const openReset = (user) => {
    setResetUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  };

  const closeReset = () => {
    if (submitting) return;
    setResetUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  };

  const handleResetSubmit = async () => {
    if (submitting) return; // guard double-submit
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const response = await fetch(`${apiEndpoint}/admin/reset-pw`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        },
        body: JSON.stringify({
          schoolId: schoolId,
          userEmail: resetUser.email,
          plainPassword: newPassword
        })
      });

      if (response.ok) {
        alert(`Password reset for ${resetUser.email}`);
        setSubmitting(false);
        setResetUser(null);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        let msg = 'Failed to reset password. Please try again.';
        try {
          const data = await response.json();
          if (data?.error) msg = data.error;
        } catch { /* non-json body */ }
        setError(msg);
        setSubmitting(false);
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError('Error resetting password. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="school-users">
      <h3>School Users ({users.length})</h3>
      <div className="users-list">
        {users.length === 0 ? (
          <div className="no-users">No users found</div>
        ) : (
          sortedUsers.map(user => (
            <div key={user.id} className="user-card">
              <div className="user-header">
                <span className="user-name">{user.name}</span>
                <span className={`user-role role-${user.role.toLowerCase()}`}>
                  {user.role}
                </span>
              </div>
              <div className="user-email">{user.email}</div>
              {user.notificationsEmail && (
                <div className="user-notifications">
                  <span className="label">Notifications:</span> {user.notificationsEmail}
                </div>
              )}
              <div className="user-settings">
                <span className="label">Frequency:</span> {user.violationEmailFrequency}
                {' | '}
                <span className="label">Hour:</span> {user.violationEmailHour}:00
              </div>
              <div className="user-actions">
                <button className="reset-pw-button" onClick={() => openRole(user)}>
                  Edit role
                </button>
                {user.role !== 'district' && (
                  <button className="reset-pw-button" onClick={() => openMkDistrict(user)}>
                    Make district account
                  </button>
                )}
                <button className="reset-pw-button" onClick={() => openReset(user)}>
                  Reset password
                </button>
                <button className="delete-account-button" onClick={() => openDelete(user)}>
                  Delete account
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {resetUser && (
        <div className="modal-overlay" onClick={closeReset}>
          <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Password</h3>
              <button className="modal-close" onClick={closeReset}>×</button>
            </div>
            <div className="modal-body">
              <p>Set a new password for:</p>
              <p className="invite-details">
                <strong>{resetUser.name}</strong>
                <br />
                {resetUser.email}
              </p>
              <div className="form-group">
                <label>New password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Confirm password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                />
              </div>
              {error && <p className="warning-text">{error}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={closeReset} disabled={submitting}>
                Cancel
              </button>
              <button className="modal-button delete-confirm" onClick={handleResetSubmit} disabled={submitting}>
                {submitting ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteUser && (
        <div className="modal-overlay" onClick={closeDelete}>
          <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete account</h3>
              <button className="modal-close" onClick={closeDelete}>×</button>
            </div>
            <div className="modal-body">
              <p>This permanently deletes the account for:</p>
              <p className="invite-details">
                <strong>{deleteUser.name}</strong>
                <br />
                {deleteUser.email}
              </p>
              <p className="warning-text">This cannot be undone. To confirm, type the account's email below.</p>
              <div className="form-group">
                <label>Type <strong>{deleteUser.email}</strong> to confirm</label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deleteUser.email}
                  autoFocus
                />
              </div>
              {deleteError && <p className="warning-text">{deleteError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={closeDelete} disabled={deleting}>
                Cancel
              </button>
              <button
                className="modal-button delete-confirm"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText.trim().toLowerCase() !== (deleteUser.email || '').trim().toLowerCase()}
              >
                {deleting ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {mkDistrictUser && (
        <div className="modal-overlay" onClick={closeMkDistrict}>
          <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Make district account</h3>
              <button className="modal-close" onClick={closeMkDistrict}>×</button>
            </div>
            <div className="modal-body">
              <p>Convert this user into a district administrator:</p>
              <p className="invite-details">
                <strong>{mkDistrictUser.name}</strong>
                <br />
                {mkDistrictUser.email}
              </p>
              <p>This will:</p>
              <ul className="mk-district-list">
                <li>Keep their <strong>same login</strong> (email &amp; password).</li>
                <li>Create a <strong>new district</strong> and move this school into it. All its data stays.</li>
                <li>Move this user out of the school's list to manage the whole district.</li>
                <li>Prompt them to <strong>name their district</strong> the next time they sign in.</li>
              </ul>
              {mkDistrictError && <p className="warning-text">{mkDistrictError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={closeMkDistrict} disabled={mkDistrictBusy}>
                Cancel
              </button>
              <button className="modal-button delete-confirm" onClick={handleMakeDistrict} disabled={mkDistrictBusy}>
                {mkDistrictBusy ? 'Working…' : 'Make district account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {roleUser && (
        <div className="modal-overlay" onClick={closeRole}>
          <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit role</h3>
              <button className="modal-close" onClick={closeRole}>×</button>
            </div>
            <div className="modal-body">
              <p>Change the role for:</p>
              <p className="invite-details">
                <strong>{roleUser.name}</strong>
                <br />
                {roleUser.email}
              </p>
              <div className="form-group">
                <label>Role</label>
                <select value={roleDraft} onChange={(e) => setRoleDraft(e.target.value)} className="role-select" autoFocus>
                  <option value="teacher">Teacher</option>
                  <option value="administrator">Administrator</option>
                  <option value="district">District user</option>
                </select>
              </div>
              {roleDraft === 'district' && (
                <p className="warning-text">
                  Makes this a district-level user for this school's district. They'll move out of this
                  school's list and manage the whole district. (The school must belong to a district.)
                </p>
              )}
              {roleError && <p className="warning-text">{roleError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={closeRole} disabled={savingRole}>
                Cancel
              </button>
              <button
                className="modal-button delete-confirm"
                onClick={handleSaveRole}
                disabled={savingRole || roleDraft === (roleUser.role || '').toLowerCase()}
              >
                {savingRole ? 'Saving…' : 'Save role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SchoolUsers;
